import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, CloseButton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductExportNotice as ProductExportNoticeModel } from './productExportMeta';

type ProductExportNoticeProps = {
    notice: ProductExportNoticeModel | null;
    onDismiss: () => void;
};

/**
 * How the last export ended, in the bottom right corner (bos-app#120, design
 * board **Export**): "Export ready" with the product count and the file name,
 * or why it failed. It stays until dismissed or the next export: the app
 * mounts no toast host, on purpose (see `SuccessNotice`).
 *
 * The live region is always mounted, so a screen reader hears the notice
 * when it arrives: politely for a saved file, at once for a failure.
 */
export const ProductExportNotice: React.FC<ProductExportNoticeProps> = ({
    notice,
    onDismiss,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            aria-live={notice?.kind === 'failed' ? 'assertive' : 'polite'}
            className="fixed right-4 bottom-4 z-50 w-[calc(100vw-2rem)] max-w-sm sm:right-6 sm:bottom-6"
            role="status"
            {...getNodeTestIdProp('product-export-notice-region')}
        >
            {notice === null ? null : (
                <Alert
                    status={notice.kind === 'saved' ? 'success' : 'danger'}
                    {...getNodeTestIdProp('product-export-notice')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {notice.kind === 'saved'
                                ? t('products.export.notice.savedTitle')
                                : t('products.export.notice.failedTitle')}
                        </Alert.Title>
                        <Alert.Description>
                            {notice.kind === 'saved'
                                ? t('products.export.notice.saved', {
                                      count: notice.productCount,
                                      filename: notice.filename,
                                  })
                                : t(notice.failure.key)}
                        </Alert.Description>
                    </Alert.Content>
                    <CloseButton
                        aria-label={t('products.export.notice.dismiss')}
                        onPress={onDismiss}
                        {...getNodeTestIdProp('product-export-notice-dismiss')}
                    />
                </Alert>
            )}
        </div>
    );
};
